import { css } from "@emotion/react";
import { useMemo } from "react";
import * as React from "react";
import { useSelector } from "react-redux";
import { RowInfo } from "react-table";

import { DesignToken, SpacingToken } from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import { IDateRangeWithCompareFilter } from "constants/filters";
import { ReportDistribution } from "constants/reports/distribution";
import { ReportType } from "constants/reports/report-type";
import { getChartFormatter } from "helpers/chart";
import { duration } from "helpers/date";
import { useLastVisitedReport } from "hooks/reports/use-last-visited-report";
import { IStoreState } from "interfaces/storeState";
import { ReportLocalTimeLabel } from "routes/reports/components/ReportLocaltimeLabel";
import { ITicketResolutionTimePerEntity } from "services/api/report/interfaces";
import { selectAgents } from "store/entities/agents";
import { selectFeatureTogglePerformanceReport } from "store/entities/license";
import { Plan, selectCurrentSubscriptionPlan } from "store/entities/subscriptions";
import { selectTeams } from "store/entities/teams";
import {
  ITicketResolutionTimeData,
  IWithReportsViewState,
  getDistribution,
  getFilterDateRange,
  getIsReportFetching,
  getReportData,
} from "store/views/reports";
import { withVar } from "themes/helpers";
import { plural } from "utils/string";

import { ChartPanel, Filters } from "../components";
import { ChartBreakdown } from "../components/ChartBreakdown";
import { AgentCell, TeamCell, ValueCell } from "../components/PerformanceTableCells";
import { PerformanceTablePromo } from "../components/PerformanceTablePromo";
import { getChartOptions, getExportFilename, getLegendNavigationResolver, getNavigationResolver } from "../helpers";
import { containerFluid } from "../styled/layout";

import { formatDurationWithFallback, solvedTicketsParamsParser } from "./helpers";

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
function pointFormatter(point: any): string {
  const index = point.index ?? point.point.index;
  const count = point.series.options.extras.count && point.series.options.extras.count[index];
  const percentage = point.series.options.extras.percentage && point.series.options.extras.percentage[index];

  if (count === 0) {
    return "0 resolved tickets";
  }

  return `
    <strong>Resolution time:</strong> ${duration(point.y)}<br>
    <strong>${count} ${plural(count, "ticket", "tickets")}</strong> (${percentage}% of all tickets from this day)
  `;
}

const chartOptions = {
  yAxis: {
    labels: {
      format: "{value} h",
      formatter(): string {
        // @ts-expect-error TS(2339): Property 'value' does not exist on type '{ format:... Remove this comment to see the full error message
        return duration(this.value);
      },
    },
  },
  tooltip: {
    formatter: getChartFormatter(pointFormatter, true),
  },
};

interface IStateProps {
  isFetching: boolean;
  data: ITicketResolutionTimeData;
  distribution: ReportDistribution;
  dateRange: IDateRangeWithCompareFilter;
}

type IProps = IStateProps;

const TicketResolutionTime: React.FC<IProps> = React.memo(() => {
  TicketResolutionTime.displayName = "TicketResolutionTime";
  useLastVisitedReport(ReportType.TicketResolutionTime);
  const isFetching = useSelector((state: IStoreState) => getIsReportFetching(state, ReportType.TicketResolutionTime));
  const data = useSelector((state: IWithReportsViewState) => getReportData(state, ReportType.TicketResolutionTime));
  const creationAverage = data?.resolutionTime.summary[0].rawValue;
  const assignmentAverage = data?.resolutionTime.summary[0].rawValue;
  const distribution = useSelector(getDistribution);
  const dateRange = useSelector(getFilterDateRange);
  const isTeam = useSelector(selectCurrentSubscriptionPlan) === Plan.Team;
  const teams = useSelector(selectTeams);
  const agents = useSelector(selectAgents);
  const hasPerformanceReport = useSelector(selectFeatureTogglePerformanceReport);
  const perTeamData = data?.resolutionTimePerTeam;
  const perAgentData = data?.resolutionTimePerAgent;
  const resolutionTimeChartLegend = data && data.resolutionTime.summary;
  const resolutionTimeChartOptions = useMemo(
    () =>
      data &&
      getChartOptions(
        data.resolutionTime.series,
        data.resolutionTime.labels,
        distribution,
        getNavigationResolver("/tickets/solved", solvedTicketsParamsParser),
        {
          ...chartOptions,
          xAxis: {
            title: {
              text: distribution === ReportDistribution.Hour ? "Ticket creation hour" : "Ticket creation date",
              style: {
                color: withVar(DesignToken.ContentBasicDisabled),
              },
            },
          },
        }
      ),
    [data, distribution]
  );

  const teamColumns = useMemo(() => {
    return [
      {
        Header: "Name",
        accessor: "id",
        Cell: ({ value, row }: { value: string; row: RowInfo["row"] }): JSX.Element => (
          <>
            <TeamCell value={value} row={row} teamName={teams[value]?.name} totalLabel="Average" />
          </>
        ),
        className: "even",
      },
      {
        Header: (
          <>
            <span css={tableAssignTimeHeaderStyles}></span>
            <span>Avg. time from assignment to team</span>
          </>
        ),
        accessor: "averageSecondsFromAssignment",
        Cell: ({ value, original }: { value: number; original: ITicketResolutionTimePerEntity }): JSX.Element => (
          <ValueCell value={value} valueToCompare={assignmentAverage} original={original} />
        ),
        className: "odd",
      },
      {
        Header: (
          <>
            <span css={tableCreationTimeHeaderStyles}></span>
            <span>Avg. time from ticket creation</span>
          </>
        ),
        accessor: "averageSecondsFromCreation",
        Cell: ({ value, original }: { value: number; original: ITicketResolutionTimePerEntity }): JSX.Element => (
          <ValueCell value={value} valueToCompare={creationAverage} original={original} />
        ),
        className: "even",
      },
    ];
  }, [data, teams]);

  const agentColumns = useMemo(() => {
    return [
      {
        Header: "Name",
        accessor: "id",
        Cell: ({ value, row }: { value: string; row: RowInfo["row"] }): JSX.Element => {
          const agent = agents[value];
          const { avatar, name } = agent ?? {};
          return <AgentCell value={value} row={row} agentName={name} avatar={avatar} totalLabel="Average" />;
        },
        className: "even",
      },
      {
        Header: (
          <>
            <span css={tableAssignTimeHeaderStyles}></span>
            <span>Avg. time from assignment to agent</span>
          </>
        ),
        accessor: "averageSecondsFromAssignment",
        Cell: ({ value, original }: { value: number; original: ITicketResolutionTimePerEntity }): JSX.Element => (
          <ValueCell value={value} valueToCompare={assignmentAverage} original={original} />
        ),
        className: "odd",
      },
      {
        Header: (
          <>
            <span css={tableCreationTimeHeaderStyles}></span>
            <span>Avg. time from ticket creation</span>
          </>
        ),
        accessor: "averageSecondsFromCreation",
        Cell: ({ value, original }: { value: number; original: ITicketResolutionTimePerEntity }): JSX.Element => (
          <ValueCell value={value} valueToCompare={creationAverage} original={original} />
        ),
        className: "even",
      },
    ];
  }, [data, teams]);

  return (
    <div>
      <Filters />
      <div css={containerFluid}>
        <ChartPanel
          title="Resolution time"
          tooltip="Average time from ticket creation and its assignment to status solved or closed."
          loading={isFetching}
          legend={resolutionTimeChartLegend}
          legendUrlResolver={getLegendNavigationResolver("/tickets/solved", solvedTicketsParamsParser)}
          options={resolutionTimeChartOptions}
          exportFileName={getExportFilename("ticket_resolution_time", dateRange, distribution)}
          breakdownValueParser={formatDurationWithFallback}
          breakdownChartOptions={resolutionTimeChartOptions}
          disabledBreakdownTotal
          customComponent={
            hasPerformanceReport ? (
              <>
                <ChartBreakdown
                  sort={{ id: "averageSecondsFromCreation", desc: false }}
                  customTitle="Resolution time per team"
                  breakdownData={{ columns: teamColumns, data: perTeamData }}
                  loading={isFetching}
                  truncateData={perTeamData && perTeamData.length > 5}
                />
                <Block marginBottom={withVar(SpacingToken.Spacing7)} />
                <ChartBreakdown
                  sort={{ id: "averageSecondsFromCreation", desc: false }}
                  fallbackComponent={isTeam && <PerformanceTablePromo />}
                  customTitle="Resolution time per agent"
                  breakdownData={{ columns: agentColumns, data: perAgentData }}
                  loading={isFetching}
                  truncateData={perAgentData && perAgentData.length > 5}
                  disableExpand={isTeam}
                />
                <Block marginBottom={withVar(SpacingToken.Spacing7)} />
              </>
            ) : null
          }
        />
        <ReportLocalTimeLabel />
      </div>
    </div>
  );
});

export default TicketResolutionTime;

const tableHeaderStyles = css`
  width: 12px;
  height: 12px;
  border-radius: 2px;
  margin-right: ${withVar(SpacingToken.Spacing1)};
  margin-top: ${withVar(SpacingToken.Spacing1)};
`;

const tableAssignTimeHeaderStyles = css`
  ${tableHeaderStyles};
  background-color: ${withVar(DesignToken.DecorGreen500)};
`;

const tableCreationTimeHeaderStyles = css`
  ${tableHeaderStyles};
  background-color: ${withVar(DesignToken.DecorBlue500)};
`;
